import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

interface HtaccessState {
    forceHttps: boolean;
    wwwMode: 'none' | 'force-www' | 'force-non-www';
    gzip: boolean;
    browserCache: boolean;
    blockHotlinking: boolean;
    customDomain: string;
    customRedirects: string;
    customErrorPage404: string;
    copied: boolean;
}

export default class HtaccessGeneratorComponent extends Component<{ feature?: string }, HtaccessState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: HtaccessState = {
        forceHttps: true,
        wwwMode: 'none',
        gzip: true,
        browserCache: true,
        blockHotlinking: false,
        customDomain: 'example.com',
        customRedirects: '',
        customErrorPage404: '',
        copied: false,
    };

    private generate(): string {
        const blocks: string[] = [];
        const s = this.state;

        if (s.forceHttps || s.wwwMode !== 'none' || s.customRedirects.trim()) {
            const lines: string[] = ['<IfModule mod_rewrite.c>', 'RewriteEngine On'];
            if (s.forceHttps) {
                lines.push('# Force HTTPS');
                lines.push('RewriteCond %{HTTPS} off');
                lines.push('RewriteRule ^(.*)$ https://%{HTTP_HOST}%{REQUEST_URI} [L,R=301]');
            }
            if (s.wwwMode === 'force-www') {
                lines.push('# Force www');
                lines.push('RewriteCond %{HTTP_HOST} !^www\\. [NC]');
                lines.push('RewriteRule ^(.*)$ https://www.%{HTTP_HOST}%{REQUEST_URI} [L,R=301]');
            } else if (s.wwwMode === 'force-non-www') {
                lines.push('# Force non-www');
                lines.push('RewriteCond %{HTTP_HOST} ^www\\.(.+)$ [NC]');
                lines.push('RewriteRule ^(.*)$ https://%1%{REQUEST_URI} [L,R=301]');
            }
            s.customRedirects.split('\n').forEach(line => {
                const trimmed = line.trim();
                if (!trimmed || trimmed.startsWith('#')) return;
                const parts = trimmed.split(/\s+/);
                if (parts.length >= 2) {
                    lines.push(`Redirect 301 ${parts[0]} ${parts[1]}`);
                }
            });
            lines.push('</IfModule>');
            blocks.push(lines.join('\n'));
        }

        if (s.gzip) {
            blocks.push([
                '# Gzip compression',
                '<IfModule mod_deflate.c>',
                '  AddOutputFilterByType DEFLATE text/html text/plain text/xml text/css text/javascript application/javascript application/json application/xml application/rss+xml image/svg+xml',
                '</IfModule>',
            ].join('\n'));
        }

        if (s.browserCache) {
            blocks.push([
                '# Browser caching',
                '<IfModule mod_expires.c>',
                '  ExpiresActive On',
                '  ExpiresByType image/jpeg "access plus 1 year"',
                '  ExpiresByType image/png "access plus 1 year"',
                '  ExpiresByType image/webp "access plus 1 year"',
                '  ExpiresByType image/svg+xml "access plus 1 year"',
                '  ExpiresByType text/css "access plus 1 month"',
                '  ExpiresByType application/javascript "access plus 1 month"',
                '  ExpiresByType text/html "access plus 1 hour"',
                '</IfModule>',
            ].join('\n'));
        }

        if (s.blockHotlinking && s.customDomain.trim()) {
            const domain = s.customDomain.trim().replace(/\./g, '\\.');
            blocks.push([
                '# Block hotlinking',
                '<IfModule mod_rewrite.c>',
                '  RewriteEngine On',
                `  RewriteCond %{HTTP_REFERER} !^$`,
                `  RewriteCond %{HTTP_REFERER} !^https?://(.+\\.)?${domain} [NC]`,
                `  RewriteRule \\.(jpg|jpeg|png|gif|webp|svg)$ - [F,NC]`,
                '</IfModule>',
            ].join('\n'));
        }

        if (s.customErrorPage404.trim()) {
            blocks.push(`# Custom 404 page\nErrorDocument 404 ${s.customErrorPage404.trim()}`);
        }

        return blocks.join('\n\n');
    }

    private toggle(field: keyof HtaccessState) {
        this.setState({ [field]: !this.state[field] } as any);
    }

    copyOutput = () => {
        navigator.clipboard.writeText(this.generate()).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    downloadOutput = () => {
        const blob = new Blob([this.generate()], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = '.htaccess';
        a.click();
        URL.revokeObjectURL(url);
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const s = this.state;
        const output = this.generate();
        const lineCount = output.split('\n').filter(Boolean).length;
        const checkboxRow = (label: React.ReactNode, checked: boolean, onChange: () => void) => (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={checked} onChange={onChange} />
                {label}
            </label>
        );

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Redirects</span></div>
                    {checkboxRow('Force HTTPS', s.forceHttps, () => this.toggle('forceHttps'))}
                    <div className={genStyles.field}>
                        <label htmlFor="www">www handling</label>
                        <select id="www" value={s.wwwMode} onChange={e => this.setState({ wwwMode: e.target.value as any })}>
                            <option value="none">No change</option>
                            <option value="force-www">Force www (example.com → www.example.com)</option>
                            <option value="force-non-www">Force non-www (www.example.com → example.com)</option>
                        </select>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="redirects">Custom 301 redirects</label>
                        <textarea id="redirects" rows={3} value={s.customRedirects}
                            placeholder={"/old-page /new-page\n/legacy https://newsite.com/"}
                            onChange={e => this.setState({ customRedirects: e.target.value })} />
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>One per line: "/old /new"</span>
                    </div>
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Performance</span></div>
                    {checkboxRow('Gzip compression', s.gzip, () => this.toggle('gzip'))}
                    {checkboxRow('Browser-cache headers (1 year images / 1 month assets / 1 hour HTML)', s.browserCache, () => this.toggle('browserCache'))}
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Security</span></div>
                    {checkboxRow('Block image hotlinking', s.blockHotlinking, () => this.toggle('blockHotlinking'))}
                    {s.blockHotlinking && (
                        <div className={genStyles.field}>
                            <label htmlFor="domain">Your domain</label>
                            <input id="domain" type="text" value={s.customDomain}
                                onChange={e => this.setState({ customDomain: e.target.value })} placeholder="example.com" />
                        </div>
                    )}
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Error pages</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="err404">Custom 404 page (path or URL, optional)</label>
                        <input id="err404" type="text" value={s.customErrorPage404}
                            onChange={e => this.setState({ customErrorPage404: e.target.value })} placeholder="/404.html" />
                    </div>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated .htaccess"
                previewMeta={`${lineCount} line${lineCount === 1 ? '' : 's'}`}
                preview={<CodeBlock code={output} language="apacheconf" wrapLines maxHeight={500} minHeight={320} ariaLabel="Generated .htaccess" />}
                previewActions={<>
                    <button className={`${genStyles.iconBtn} ${s.copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput}>{s.copied ? '✓ Copied' : '📋 Copy'}</button>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`}
                        onClick={this.downloadOutput}>⬇ Download .htaccess</button>
                </>}
            />
        );
    }
}
